"use client";

import { SVGIconProps } from "@faire/slate/icons/types";
import { componentCls } from "@faire/slate/lib/tracking";
import { tsv } from "@faire/slate/lib/tsv";
import {
  ButtonBase,
  buttonStyleVars,
} from "@faire/slate/private/button/ButtonBase";
import { ButtonIcon } from "@faire/slate/private/button/ButtonIcon";
import {
  ButtonIconPosition,
  ButtonSize,
  ButtonVariant,
  IButtonWebProps,
} from "@faire/slate/private/button/types";
import { usePolimorphicAttributes } from "@faire/slate/private/button/usePolimorphicAttributes";
import { useShouldShowSpinner } from "@faire/slate/private/button/useShouldShowSpinner";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";

interface IIconProps {
  /**
   * The icon component to render
   */
  Component: React.FC<SVGIconProps>;

  /**
   * The position of the icon.
   *
   * `"start"`, `"end"`
   *
   * On LTR, start is left and end is right.
   * On RTL, start is right and end is left.
   *
   * @default "start"
   */
  position?: ButtonIconPosition;
}

type IProps = {
  /**
   * Button variant
   *
   * @default "primary"
   */
  variant?: ButtonVariant;
  /**
   * Button size (doesn't affect tertiary buttons)
   *
   * small (height 40px), medium (height 48px)
   *
   * @default "medium"
   */
  size?: ButtonSize;
  /**
   * Whether the button has the destructive variant.
   *
   * @default false
   */
  destructive?: boolean;
  /**
   * Shows a loading spinner.
   *
   * @default false
   */
  loading?: boolean;
  /**
   * Whether the button is disabled.
   *
   * @default false
   */
  disabled?: boolean;
  /**
   * The {@link IIconProps} to display.
   *
   * ```ts
   * interface IIconProps {
   *   // The icon component to render
   *   Component: React.FC<SVGIconProps>;
   *   // The position of the icon
   *   // "start" (default) | "end"
   *   position?: ButtonIconPosition;
   * }
   * ```
   */
  iconProps?: IIconProps;
  /**
   * The button label.
   */
  children: string | React.ReactElement<typeof StrictLocalMsg>;

  /**
   * **web**
   * Tertiary button color override.
   *
   * !!! This is a temporary solution while we don't have a proper Link component.
   *
   * @default undefined
   */
  tertiaryColor?: string;

  /**
   * **web**
   * Whether the button should take the full width of the container
   * (affects primary and secondary variants only).
   *
   * @default false
   */
  fullWidth?: boolean;

  // DO NOT ADD any new prop without aligning with Slate team first
} & IButtonWebProps;

/**
 * Buttons help our users take action or make a choice across a hierarchy of emphasis.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * @trackcomponent
 * @version 2.0.0
 */
export const Button: React.FC<IProps> = ({
  children,
  destructive = false,
  disabled,
  fullWidth = false,
  iconProps,
  loading,
  onClick,
  size = "medium",
  tertiaryColor,
  variant = "primary",
  ...props
}) => {
  const { handleClick, shouldShowSpinner, isLoading } = useShouldShowSpinner(
    disabled,
    loading,
    onClick
  );
  const { as, linkProps } = usePolimorphicAttributes(props);

  const isTertiary = variant === "tertiary";

  const buttonCls = buttonStyle.cls({
    variant,
    destructive,
    fullWidth,
    size,
    className: componentCls(),
  });

  const buttonVars = buttonStyleVars({
    color: isTertiary ? tertiaryColor : undefined,
  });

  const iconPosition = iconProps?.position ?? "start";
  const icon = iconProps?.Component ? (
    <ButtonIcon
      Icon={iconProps.Component}
      className={iconStyle.cls({ position: iconPosition })}
      titleAccess=""
    />
  ) : null;

  const { linkTo, ...rest } = props;

  return (
    <ButtonBase
      {...rest}
      {...linkProps}
      aria-disabled={isLoading || disabled}
      as={as}
      className={buttonCls}
      disabled={disabled}
      loading={shouldShowSpinner}
      onClick={handleClick}
      style={buttonVars}
    >
      {iconPosition === "start" ? icon : null}
      <ButtonLabel variant={variant}>{children}</ButtonLabel>
      {iconPosition === "end" ? icon : null}
    </ButtonBase>
  );
};

const ButtonLabel: React.FC<
  React.PropsWithChildren<{ variant: ButtonVariant }>
> = ({ children, variant }) => {
  const isTertiary = variant === "tertiary";
  const labelClass = labelStyle.cls({ isTertiary });

  return <span className={labelClass}>{children}</span>;
};

const buttonStyle = tsv({
  variants: {
    variant: {
      primary: "fs-button--primary px-(--slate-dimensions-6)",
      secondary: "fs-button--secondary px-(--slate-dimensions-6)",
      tertiary: "fs-button--link inline h-auto border-none text-start",
    },
    fullWidth: { true: "w-full" },
    destructive: { true: "fs-button--destructive" },
    size: {
      small: "fs-button--small",
      medium: "fs-button--medium",
    },
  },
});

const labelStyle = tsv({
  base: "line-clamp-2",
  variants: {
    isTertiary: {
      true: "line-clamp-none inline items-start underline",
    },
  },
});

const iconStyle = tsv({
  variants: {
    position: {
      start: "mr-2",
      end: "ml-2",
    },
  },
});
